import React, { useState, useEffect, Fragment } from 'react';
import { Modal, Button, Row, Col, Card, Table, Spinner, Alert } from 'react-bootstrap';
import { X, Package, ShoppingBag, Tag, Box, Calendar, Clock } from 'lucide-react';
import { getProductById } from '../../api/products';
import useAuth from '../../hooks/useAuth';
import { getDistributorPricesCurrentUser } from '../../api/partner';

// Configuration - Ajustez cette URL selon votre configuration backend
//const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://senoscar.site';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://apps.prod.atlanticds-sn.com/senoscar';


const ProductDetails = ({ productId, show, onHide }) => {
    const { isAdmin, isManager, isSalesRep, isDistributor, user } = useAuth();

    const hasStockAccess = isAdmin || isManager || isSalesRep;

    const [priceList, setPriceList] = useState([]);
    const [loadingPrices, setLoadingPrices] = useState(true);

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeImageIndex, setActiveImageIndex] = useState(0);

    useEffect(() => {
        const fetchProductDetails = async () => {
            if (!productId) return;

            try {
                setLoading(true);
                setError('');
                const data = await getProductById(productId);
                setProduct(data);

                // Set active image to main image if exists
                if (data.images && data.images.length > 0) {
                    const mainImageIndex = data.images.findIndex(img => img.mainImage);
                    setActiveImageIndex(mainImageIndex !== -1 ? mainImageIndex : 0);
                }
            } catch (err) {
                setError('Failed to load product details. Please try again.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (show) {
            fetchProductDetails();
        }
    }, [productId, show]);

    useEffect(() => {
        const fetchPrices = async () => {
            if (isDistributor && user?.id) {
                try {
                    const prices = await getDistributorPricesCurrentUser(user.id);
                    setPriceList(prices);
                } catch (err) {
                    console.error("Erreur de chargement des prix du distributeur", err);
                } finally {
                    setLoadingPrices(false);
                }
            } else {
                setLoadingPrices(false);
            }
        };

        fetchPrices();
    }, [isDistributor, user]);

    console.log('priceList updated:', priceList);

    const getDistributorPrice = (productId) => {
        const entry = priceList.find(p => p.product.id === productId);
        return entry ? entry.price : null;
    };


    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const handleThumbnailClick = (index) => {
        setActiveImageIndex(index);
    };

    // Helper function to get full image URL
    // Mise à jour de la fonction getFullImageUrl dans ProductDetails.jsx

    const getFullImageUrl = (path) => {
        if (!path) return null;

        // Si le chemin commence déjà par http ou https, c'est une URL complète
        if (path.startsWith('http://') || path.startsWith('https://')) {
            return path;
        }

        // Toujours utiliser le préfixe /uploads/
        return `${API_BASE_URL}/uploads/${path}`;
    };

    // Render loading state
    if (loading) {
        return (
            <Modal show={show} onHide={onHide} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Détails produits</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Chargement des détails du produit...</p>
                </Modal.Body>
            </Modal>
        );
    }

    // Render error state
    if (error) {
        return (
            <Modal show={show} onHide={onHide} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Détails produit</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="danger">{error}</Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        <X size={16} className="me-1" />
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    // Render no product state
    if (!product) {
        return (
            <Modal show={show} onHide={onHide} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Details produits</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning">Aucun produit trouvé.</Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        <X size={16} className="me-1" />
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center">
                    <Box size={20} className="me-2 text-primary" />
                    Details produit
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    {/* Product Images */}
                    <Col md={6}>
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Body className="p-0">
                                {product.images && product.images.length > 0 ? (
                                    <>
                                        <div className="main-image-container position-relative mb-2">
                                            <img
                                                src={getFullImageUrl(product.images[activeImageIndex].path)}
                                                alt={`${product.name} - Image ${activeImageIndex + 1}`}
                                                className="img-fluid w-100 rounded"
                                                style={{ height: '300px', objectFit: 'contain', backgroundColor: '#f8f9fa' }}
                                                onError={(e) => {
                                                    console.error("Error loading image:", e);
                                                    e.target.onerror = null;
                                                    e.target.src = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>`;
                                                }}
                                            />
                                        </div>
                                        {product.images.length > 1 && (
                                            <div className="d-flex flex-wrap gap-2 justify-content-center mt-2">
                                                {product.images.map((image, index) => (
                                                    <div
                                                        key={index}
                                                        onClick={() => handleThumbnailClick(index)}
                                                        className={`thumbnail-container border ${index === activeImageIndex ? 'border-primary' : 'border-secondary'} rounded cursor-pointer`}
                                                        style={{ width: '60px', height: '60px', cursor: 'pointer' }}
                                                    >
                                                        <img
                                                            src={getFullImageUrl(image.path)}
                                                            alt={`Thumbnail ${index + 1}`}
                                                            className="img-fluid w-100 h-100"
                                                            style={{ objectFit: 'cover', borderRadius: '3px' }}
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect></svg>`;
                                                            }}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="d-flex justify-content-center align-items-center bg-light rounded" style={{ height: '300px' }}>
                                        <div className="text-center">
                                            <Package size={80} className="text-muted mb-3" />
                                            <p className="text-muted">Aucune image disponible</p>
                                        </div>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Product Details */}
                    <Col md={6} >
                        <h4 className="mb-3">{product.name}</h4>


                        <div className="d-flex mb-3">

                            <div className="me-3 badge bg-success d-flex align-items-center p-2 fs-6">
                                <Fragment size={26} className="me-1" />
                                <>
                                    {product.regular_price} F cfa  {" "}
                                    <span className="ms-1 badge bg-success"></span>
                                </>

                            </div>
                            {/* {hasStockAccess && (
                            <div className={`badge ${product.stockQuantity <= 10 ? 'bg-danger' : 'bg-primary'} d-flex align-items-center p-2 fs-6`}>
                            <Layers size={16} className="me-1" />
                            {product.stockQuantity} En stock
                            </div>
                             )} */}
                        </div>



                        <Table bordered size="sm" className="mt-3">
                            <tbody>
                                <tr>
                                    <td className="bg-light" width="40%">
                                        <Tag size={14} className="me-1" /> Référence
                                    </td>
                                    <td>
                                        <code>{product.sku}</code>
                                    </td>
                                </tr>
                                {product.category && (
                                    <tr>
                                        <td className="bg-light">
                                            <ShoppingBag size={14} className="me-1" /> Catégorie
                                        </td>
                                        <td>{product.category}</td>
                                    </tr>
                                )}
                                {product.dimensions && (
                                    <tr>
                                        <td className="bg-light">
                                            <Box size={14} className="me-1" /> Dimensions
                                        </td>
                                        <td>{product.dimensions}</td>
                                    </tr>
                                )}
                                <tr>
                                    <td className="bg-light">
                                        <Calendar size={14} className="me-1" /> Crée
                                    </td>
                                    <td>{formatDate(product.createdAt)}</td>
                                </tr>
                                <tr>
                                    <td className="bg-light">
                                        <Clock size={14} className="me-1" /> Dernière modification
                                    </td>
                                    <td>{formatDate(product.updatedAt)}</td>
                                </tr>
                            </tbody>
                        </Table>

                        {product.description && (
                            <div className="mt-3">
                                <h6 className="mb-2">Description</h6>
                                <div className="bg-light p-3 rounded">
                                    {product.description.split('\n').map((paragraph, index) => (
                                        <p key={index} className={index !== 0 ? 'mt-2 mb-0' : 'mb-0'}>
                                            {paragraph}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        )}
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    <X size={16} className="me-1" />
                    Fermer
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ProductDetails;